import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const statusClassMap = {
  done: 'badge-success',
  attention: 'badge-warning',
  missing: 'badge-danger',
};

const riskClassMap = {
  healthy: 'badge-success',
  warning: 'badge-warning',
  risk: 'badge-danger',
};

function formatDate(value) {
  return value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : 'Nao informado';
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('pt-BR') : 'Nao informado';
}

const PersonalFinanceWeeklyReviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [review, setReview] = useState(null);
  const [notes, setNotes] = useState('');
  const [actionItemsText, setActionItemsText] = useState('');

  const loadReview = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/personal-finance/weekly-review');
      const payload = response.data || null;
      setReview(payload);
      setNotes(payload?.session?.notes || '');
      setActionItemsText(Array.isArray(payload?.session?.action_items_json) ? payload.session.action_items_json.join('\n') : '');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar revisao semanal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReview();
  }, []);

  const handleCompleteReview = async () => {
    try {
      setSaving(true);
      await axios.post('/api/personal-finance/weekly-review/complete', {
        notes: notes || null,
        action_items: actionItemsText,
        status: 'completed',
      });
      toast.success('Revisao semanal concluida');
      await loadReview();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao concluir revisao semanal');
    } finally {
      setSaving(false);
    }
  };

  const summary = review?.summary || {};
  const checklist = Array.isArray(review?.checklist) ? review.checklist : [];
  const insights = Array.isArray(review?.insights) ? review.insights : [];
  const upcomingItems = Array.isArray(review?.upcoming_items) ? review.upcoming_items : [];
  const accountActivity = Array.isArray(review?.recent_activity?.accounts) ? review.recent_activity.accounts : [];
  const cardActivity = Array.isArray(review?.recent_activity?.cards) ? review.recent_activity.cards : [];
  const timeline = Array.isArray(review?.cashflow?.timeline) ? review.cashflow.timeline : [];
  const connectivity = review?.connectivity || { summary: {}, urgent_items: [] };
  const notifications = review?.notifications || { summary: {}, items: [] };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Status da semana</p>
            <div>
              <span className={summary.review_due ? 'badge-warning' : 'badge-success'}>
                {summary.review_due ? 'Pendente' : 'Concluida'}
              </span>
            </div>
            <p className="text-sm text-slate-500">
              Janela {review?.review_window ? `${formatDate(review.review_window.period_start)} - ${formatDate(review.review_window.period_end)}` : 'Nao carregada'}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Posso gastar hoje</p>
            <p className="text-2xl font-semibold text-slate-900">
              {loading ? '...' : currency.format(Number(summary.safe_to_spend_today || 0))}
            </p>
            <p className="text-sm text-slate-500">Sinal principal para decisao diaria</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Menor saldo previsto</p>
            <p className="text-2xl font-semibold text-slate-900">
              {loading ? '...' : currency.format(Number(summary.lowest_projected_balance || 0))}
            </p>
            <p className="text-sm text-slate-500">{summary.lowest_projected_balance_date ? formatDate(summary.lowest_projected_balance_date) : 'Sem data critica'}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Risco do horizonte</p>
            <div>
              <span className={riskClassMap[summary.risk_status] || 'badge-info'}>
                {summary.risk_status || 'unknown'}
              </span>
            </div>
            <p className="text-sm text-slate-500">
              {summary.upcoming_bills_count || 0} faturas e {summary.upcoming_commitments_count || 0} movimentos no radar
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Conexoes em acao</p>
            <p className="text-2xl font-semibold text-slate-900">
              {loading ? '...' : String((summary.connectivity_action_required_count || 0) + (summary.connectivity_attention_count || 0))}
            </p>
            <p className="text-sm text-slate-500">
              {summary.connectivity_action_required_count || 0} imediatas, {summary.connectivity_attention_count || 0} em observacao
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Alertas abertos</p>
            <p className="text-2xl font-semibold text-slate-900">
              {loading ? '...' : String(summary.notification_open_count || 0)}
            </p>
            <p className="text-sm text-slate-500">
              {summary.notification_unread_count || 0} ainda nao lidos no inbox
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Weekly review</p>
                  <h2 className="text-xl font-semibold text-slate-900">Checklist guiado da semana</h2>
                </div>
                <button className="btn-secondary" onClick={loadReview} disabled={loading}>
                  Atualizar
                </button>
              </div>

              <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5">
                <p className="text-sm text-emerald-900">
                  O objetivo aqui nao e conciliar tudo. E sair da semana com clareza, risco conhecido e uma lista curta de acoes concretas.
                </p>
                {(summary.connectivity_action_required_count || 0) > 0 && (
                  <p className="mt-2 text-sm text-emerald-800">
                    Health center: {summary.connectivity_action_required_count} conexoes exigem acao antes da proxima semana.
                  </p>
                )}
                {(summary.notification_open_count || 0) > 0 && (
                  <p className="mt-2 text-sm text-emerald-800">
                    Inbox: {summary.notification_open_count} alertas continuam abertos e entram na priorizacao da revisao.
                  </p>
                )}
                {review?.session?.completed_at && (
                  <p className="mt-2 text-sm text-emerald-800">
                    Ultima conclusao registrada em <span className="font-semibold">{formatDateTime(review.session.completed_at)}</span>
                  </p>
                )}
              </div>

              <div className="space-y-3">
                {checklist.map((item) => (
                  <div key={item.key} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium text-slate-900">{item.title}</p>
                      <span className={statusClassMap[item.state] || 'badge-info'}>{item.state}</span>
                    </div>
                    <p className="mt-2 text-sm text-slate-500">{item.helper}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Acoes sugeridas</p>
                  <p className="mt-1 text-sm text-slate-500">O sistema prioriza o que mais reduz surpresa financeira agora.</p>
                </div>
                <div className="mt-4 space-y-3">
                  {(review?.next_actions || []).map((item, index) => (
                    <div key={`${item}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Insights da semana</p>
                  <p className="mt-1 text-sm text-slate-500">Sinais que merecem decisao antes de entrar na proxima semana.</p>
                </div>
                <div className="mt-4 space-y-3">
                  {insights.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      Nenhum insight ativo no momento.
                    </div>
                  )}
                  {insights.map((insight) => (
                    <div key={insight.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium text-slate-900">{insight.title}</p>
                        <span className={riskClassMap[insight.severity === 'high' ? 'risk' : insight.severity === 'medium' ? 'warning' : 'healthy'] || 'badge-info'}>
                          {insight.severity}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-500">{insight.recommended_action || insight.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Saude das conexoes</p>
                  <p className="mt-1 text-sm text-slate-500">Consentimentos, stale sync e backoff entram na revisao semanal do modulo.</p>
                </div>
                <div className="mt-4 space-y-3">
                  {(connectivity.urgent_items || []).length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      Nenhuma conexao pede atencao operacional nesta semana.
                    </div>
                  )}
                  {(connectivity.urgent_items || []).map((item) => (
                    <div key={item.connection_id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium text-slate-900">{item.display_name}</p>
                        <span className={item.operational_status === 'action_required' ? 'badge-danger' : 'badge-warning'}>
                          {item.operational_status}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-500">{item.primary_issue || 'Sem detalhe adicional'}</p>
                      <p className="mt-2 text-sm text-slate-700">{item.recommended_action}</p>
                      {item.backoff_until_at && (
                        <p className="mt-2 text-xs text-slate-400">Backoff ate {formatDateTime(item.backoff_until_at)}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Alertas operacionais</p>
                    <p className="mt-1 text-sm text-slate-500">O inbox do modulo sintetiza o que precisa virar decisao curta nesta semana.</p>
                  </div>
                  <Link to="/pessoal/alertas" className="btn-secondary">
                    Abrir alertas
                  </Link>
                </div>
                <div className="mt-4 space-y-3">
                  {(notifications.items || []).length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      Nenhum alerta aberto no momento.
                    </div>
                  )}
                  {(notifications.items || []).map((item) => (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium text-slate-900">{item.title}</p>
                        <span className={item.severity === 'high' ? 'badge-danger' : item.severity === 'medium' ? 'badge-warning' : 'badge-info'}>
                          {item.severity}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-500">{item.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="card">
              <div className="card-body space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Proximos movimentos</p>
                  <h2 className="text-xl font-semibold text-slate-900">Radar curto prazo</h2>
                </div>
                <div className="space-y-3">
                  {upcomingItems.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      Nada no radar imediato. Isso normalmente indica falta de compromissos mapeados.
                    </div>
                  )}
                  {upcomingItems.map((item, index) => (
                    <div key={`${item.date}-${item.description}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-900">{item.description}</p>
                          <p className="mt-1 text-sm text-slate-500">{formatDate(item.date)} / {item.source_type}</p>
                        </div>
                        <span className={item.direction === 'inflow' ? 'badge-success' : 'badge-warning'}>
                          {item.direction}
                        </span>
                      </div>
                      <p className="mt-3 text-lg font-semibold text-slate-900">{currency.format(Number(item.amount || 0))}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Cashflow 14 dias</p>
                  <h2 className="text-xl font-semibold text-slate-900">Janela de tensao</h2>
                </div>
                <div className="space-y-3">
                  {timeline.map((day) => (
                    <div key={day.date} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium text-slate-900">{formatDate(day.date)}</p>
                          <p className="mt-1 text-sm text-slate-500">Fechamento {currency.format(Number(day.closing_balance || 0))}</p>
                        </div>
                        <span className={riskClassMap[day.risk_status] || 'badge-info'}>{day.risk_status}</span>
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm">
                          <p className="text-slate-500">Entradas</p>
                          <p className="mt-1 font-semibold text-slate-900">{currency.format(Number(day.inflows || 0))}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm">
                          <p className="text-slate-500">Saidas</p>
                          <p className="mt-1 font-semibold text-slate-900">{currency.format(Number(day.outflows || 0))}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm">
                          <p className="text-slate-500">Gap Buffer</p>
                          <p className="mt-1 font-semibold text-slate-900">{currency.format(Number(day.buffer_gap || 0))}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Registro da semana</p>
                <h2 className="text-xl font-semibold text-slate-900">Fechar a revisao</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="form-label">Notas</label>
                  <textarea
                    className="form-textarea min-h-[140px]"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="O que mudou, onde o risco esta, o que voce vai evitar ou acelerar nesta semana."
                  />
                </div>

                <div>
                  <label className="form-label">Acoes objetivas</label>
                  <textarea
                    className="form-textarea min-h-[140px]"
                    value={actionItemsText}
                    onChange={(event) => setActionItemsText(event.target.value)}
                    placeholder={'Uma acao por linha\nEx.: reduzir gasto livre ate sexta\nEx.: reservar dinheiro para fatura do cartao'}
                  />
                </div>
              </div>

              <button className="btn-primary w-full" onClick={handleCompleteReview} disabled={saving}>
                {saving ? 'Concluindo...' : 'Concluir revisao semanal'}
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Atividade recente</p>
                <h2 className="text-xl font-semibold text-slate-900">Ultimos sinais capturados</h2>
              </div>

              <div className="space-y-4">
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-sm font-semibold text-slate-900">Contas</p>
                  <div className="mt-3 space-y-3">
                    {accountActivity.length === 0 && <p className="text-sm text-slate-500">Sem atividade recente de conta.</p>}
                    {accountActivity.map((item) => (
                      <div key={item.id} className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-slate-900">{item.description}</p>
                            <p className="mt-1 text-sm text-slate-500">{item.account_display_name} / {formatDate(item.booked_at)}</p>
                          </div>
                          <span className={item.direction === 'credit' ? 'badge-success' : 'badge-warning'}>
                            {item.direction}
                          </span>
                        </div>
                        <p className="mt-3 text-base font-semibold text-slate-900">{currency.format(Number(item.amount || 0))}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <p className="text-sm font-semibold text-slate-900">Cartoes</p>
                  <div className="mt-3 space-y-3">
                    {cardActivity.length === 0 && <p className="text-sm text-slate-500">Sem atividade recente de cartao.</p>}
                    {cardActivity.map((item) => (
                      <div key={item.id} className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-slate-900">{item.description}</p>
                            <p className="mt-1 text-sm text-slate-500">{item.card_display_name} / {formatDate(item.purchase_date)}</p>
                          </div>
                          <span className="badge-info">{item.installment_count}x</span>
                        </div>
                        <p className="mt-3 text-base font-semibold text-slate-900">{currency.format(Number(item.amount_total || 0))}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceWeeklyReviewPage;
